import {
  ArrowLeftOutlined,
  CaretDownOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./view-campaign.module.scss";
import Page from "../../Page";
import { getStoreIcon } from "..";
import {
  GET_ORDER_BY_TYPE_AND_ID,
  CHANGE_ORDER_STATUS,
} from "../../../api/graphql/orders";
import { useEffect, useMemo, useState } from "react";
import { findDateDiff, formatDate } from "../../../utils/helper";
import { UserData, AppData, OrderData, ProgressData } from "../interface";
import OrderDetails from "../OrderDetails";
import StatusModal from "../../StatusModal/StatusModal";
import {
  formatInstallRate,
  formatOrderType,
} from "../../../utils/formatters";
import { managerName } from "../../ManagerSelect";
import OrderIdfaReport from "../../IdfaReports/OrderIdfaReport";
import useIdfaUpload from "../../IdfaReports/useIdfaUpload";
import { IdfaImportResult, hasIdfaReport } from "../../../api/idfaReports";
import OrderModerationBlock from "../../Moderation/OrderModerationBlock";

// No "Paused": the platform does not pause orders, so it is not offered.
export const menuProps = {
  items: [
    {
      key: "ACTIVE",
      label: <span className={styles.dropdownOption}>Active</span>,
    },
    {
      key: "DONE",
      label: <span className={styles.dropdownOption}>Done</span>,
    },
    {
      key: "CANCELED",
      label: <span className={styles.dropdownOption}>Cancelled</span>,
    },
  ],
};

const userData: UserData[] = [
  {
    key: "manager",
    header: "Manager",
    value: "Esther Hogwart",
  },
  {
    key: "name",
    header: "User Name",
    value: "Esther Hogwart",
  },
  {
    key: "email",
    header: "User Email",
    value: "esther.hogwart@promobile.com",
  },
  {
    key: "phone",
    header: "Phone Number",
    value: "+91 9876543210",
  },
  {
    key: "telegram",
    header: "Telegram",
    value: "@esther_hogwart",
  },
];

const appData: AppData[] = [
  {
    key: "title",
    header: "App",
    value: "Sleep Track",
  },
  {
    key: "appId",
    header: "ID",
    value: "Bundle.app.com",
  },
  {
    key: "store",
    header: "Platform",
    value: "APP_STORE",
  },
  {
    key: "country",
    header: "Country",
    value: "US",
  },
];

const orderData: OrderData[] = [
  {
    key: "orderType",
    header: "Order Type",
    value: "",
  },
  {
    key: "initialDate",
    header: "Start date",
    value: "27.03.2025",
  },
  {
    key: "endDate",
    header: "End date",
    value: "27.03.2025",
  },
  {
    key: "days",
    header: "Days",
    value: "15",
  },
  {
    key: "orderCompletion",
    header: "Progress",
    value: "84%",
  },
  {
    key: "installs",
    header: "Installs",
    value: "120",
  },
  {
    key: "pricePerInstall",
    header: "Price / install",
    value: "$ 0.07",
  },
  {
    key: "orderPrice",
    header: "Sell",
    value: "$ 8,776.50",
  },
];

const progressData: ProgressData[] = [
  {
    key: "installs",
    header: "Installs",
    value: "83 / 120",
    percentage: 84,
  },
  {
    key: "rate",
    header: "Rate",
    value: "83 / 120",
    percentage: 84,
  },
  {
    key: "review",
    header: "Review",
    value: "83 / 120",
    percentage: 84,
  },
];

const ViewCampaign = () => {
  const { orderType, orderId } = useParams<{
    orderType: string;
    orderId: string;
  }>();
  const {
    data: campaignData,
    loading,
    refetch,
  } = useQuery(GET_ORDER_BY_TYPE_AND_ID, {
    variables: { orderType: orderType, orderId: parseInt(orderId || "") },
  });
  console.log("campaignData", campaignData?.getOrderByTypeAndId?.status);
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [hashId, setHashId] = useState<string>("");
  const [changeOrderStatus] = useMutation(CHANGE_ORDER_STATUS);
  const numericOrderId = parseInt(orderId || "");
  const withIdfa = hasIdfaReport(orderType) && Number.isInteger(numericOrderId);
  const [idfaImport, setIdfaImport] = useState<IdfaImportResult | null>(null);
  // Upload ID: the contractor's file or archive for this order only.
  const idfaUpload = useIdfaUpload(setIdfaImport, {
    orderType: orderType || "",
    orderId: numericOrderId,
  });

  useEffect(() => {
    const order = campaignData?.getOrderByTypeAndId;
    if (order) {
      setStatus(order.status);
      setHashId(order.hashId);
    }
  }, [campaignData?.getOrderByTypeAndId]);

  const { extractedUserData, extractedOrderData, extractedAppData } =
    useMemo(() => {
      const order = campaignData?.getOrderByTypeAndId;
      if (loading || !order)
        return {
          extractedAppData: appData,
          extractedOrderData: orderData,
          extractedUserData: userData,
        };
      return {
        extractedUserData: userData.map((item) => {
          // The order's own manager, else the client's.
          const value =
            item.key === "manager"
              ? managerName(order?.admin ?? order?.user?.manager)
              : order?.user?.[item.key] || "";
          return { ...item, value };
        }),
        extractedOrderData: orderData.map((item) => {
          let value = order?.[item.key];
          if (item.key === "orderType") {
            value = formatOrderType(value, order?.subtype);
          } else if (item.key === "initialDate" || item.key === "endDate") {
            value = value ? value : "";
          } else if (item.key === "orderCompletion") {
            value = value ? `${Math.round(value)}%` : "0%";
          } else if (item.key === "days") {
            value = findDateDiff(order?.initialDate, order?.endDate) || "0";
          } else if (item.key === "installs") {
            // Ratings and reviews have no installs: the cell shows a dash.
            value = order?.totalInstalls != null ? String(order.totalInstalls) : "";
          } else if (item.key === "pricePerInstall") {
            value = formatInstallRate(order?.pricePerInstall, "$ ");
          } else if (item.key === "orderPrice") {
            value = value ? `$ ${parseFloat(value).toFixed(2)}` : "$ 0.00";
          }
          return { ...item, value };
        }),
        extractedAppData: appData.map((item) => {
          let value = order?.app?.[item.key];
          if (item.key === "store") {
            value = value ? getStoreIcon(value) : null;
          }
          return { ...item, value };
        }),
        extractedProgressData: progressData.map((item) => {
          let value = order?.[item.key];
          if (item.key === "installs") {
            value = value ? `${value} / 120` : "0 / 120";
          } else if (item.key === "rate") {
            value = value ? `${value} / 120` : "0 / 120";
          } else if (item.key === "review") {
            value = value ? `${value} / 120` : "0 / 120";
          }
          return { ...item, value };
        }),
      };
    }, [campaignData, loading]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const onLoginRedirect = () => {
    window.location.href = `${process.env.REACT_APP_FRONTEND_PROTOCOL}://${process.env.REACT_APP_FRONTEND_CLIENT_DOMAIN}/admin-login-to-client?userId=${campaignData?.getOrderByTypeAndId?.user?.id}`;
  };

  const onChangeStatus = (value: any) => {
    console.log("onChangeStatus", orderType, hashId, status, value);
    changeOrderStatus({
      variables: {
        orderType: orderType,
        status: value,
        hashId,
        userId: campaignData?.getOrderByTypeAndId?.user?.id,
      },
    })
      .then(() => {
        console.log(value);
        setStatus(value);
      })
      .catch((error) => {});
  };

  return (
    <Page
      extra={
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/campaigns")}
        >
          <ArrowLeftOutlined />
          Back to campaigns
        </button>
      }
    >
      <div className={styles.headerStyle}>
        <div>
          <h1 className={styles.orderTitle}>ID: {orderId}</h1>
          <button
            type="button"
            className={`${styles.button} ${styles.iconButton}`}
            onClick={() => copyToClipboard(orderId || "")}
            aria-label="Copy ID"
          >
            <CopyOutlined rotate={180} />
          </button>
          {withIdfa && (
            <>
              {idfaUpload.input}
              <button
                type="button"
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={idfaUpload.open}
                disabled={idfaUpload.uploading}
                title="Upload the contractor's IDFA file or archive for this order"
              >
                {idfaUpload.uploading ? "Uploading…" : "Upload ID"}
              </button>
            </>
          )}
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={`${styles.button} ${styles.outlinedButton}`}
            onClick={onLoginRedirect}
          >
            Login
          </button>

          {/* In moderation the order is approved or rejected below. */}
          {status !== "MODERATION" && (
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className={`${styles.button} ${styles.statusButton}`}
            >
              <span className={styles.menuButtonContent}>
                {menuProps.items.find((item) => item.key === status)?.label ||
                  "Status"}
                <CaretDownOutlined />
              </span>
            </button>
          )}

          <StatusModal
            onCancel={() => setIsOpen(false)}
            onClose={() => setIsOpen(false)}
            onSave={(value) => onChangeStatus(value)}
            onOk={() => setIsOpen(true)}
            statusKey={status}
            isOpen={isOpen}
            menuProps={menuProps}
          />
        </div>
      </div>

      {orderType && Number.isInteger(numericOrderId) && (
        <OrderModerationBlock
          orderType={orderType}
          orderId={numericOrderId}
          onChanged={() => refetch()}
        />
      )}

      <div className={styles.userStyle}>
        <span>User</span>
        <div className={styles.details}>
          {extractedUserData.map((item) => (
            <div
              key={item.key}
              className={item.key === "email" ? styles.wideCell : undefined}
            >
              <span>{item.header}</span>
              <span>
                <p
                  className={styles[item.key]}
                  onClick={
                    item.key === "email" &&
                    campaignData?.getOrderByTypeAndId?.user?.id
                      ? () =>
                          navigate(
                            `/user/${campaignData.getOrderByTypeAndId.user.id}`
                          )
                      : undefined
                  }
                >
                  {item.value || "-"}{" "}
                </p>
                {item.key === "email" && (
                  <button
                    type="button"
                    className={`${styles.button} ${styles.iconButton}`}
                    onClick={() => copyToClipboard(item.value || "")}
                    aria-label="Copy email"
                  >
                    <CopyOutlined rotate={180} size={18.3} />
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.userStyle}>
        <span>App</span>
        <div className={styles.details}>
          {extractedAppData.map((item) => (
            <div key={item.key}>
              <span>{item.header}</span>
              <span>{item.value || "-"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.userStyle}>
        <span>Order</span>
        <div className={`${styles.details} ${styles.detailsRow}`}>
          {extractedOrderData.map((item) => (
            <div key={item.key}>
              <span>{item.header}</span>
              <span className={styles[item.key]}>{item.value || "-"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.progressStyle}>
        <OrderDetails
          loading={loading}
          order={campaignData?.getOrderByTypeAndId}
        />
      </div>

      {withIdfa && (
        <OrderIdfaReport
          orderType={orderType as string}
          orderId={numericOrderId}
          lastImport={idfaImport}
        />
      )}
    </Page>
  );
};

export default ViewCampaign;
